import { faCheck, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tooltip, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { getEnvironmentList } from "environments/tools/common"
import { IForm, IFormEnvironmentsRow } from "form/model"
import { getEnvironmentTreeInfo, getFilteredEnvironments } from "form/tools/environments"
import { getEnvironmentRows } from "form/tools/environments-forecast"
import React from "react"
import { getDataGridTexts } from "ui/tools/list"
import { ColumnHeader } from "../../../../ui/components/ColumnHeader"
import { ColorizedText } from "./ColorizedText"

interface IEnvironmentsListProps {
  form: IForm
}

export const EnvironmentsList = React.memo(({ form }: IEnvironmentsListProps): JSX.Element => {
  const totalEnvironments = getEnvironmentList().length
  const filteredEnvironments = getEnvironmentRows(
    form,
    getFilteredEnvironments(
      form.screens.environmentFilter,
      form.screens.environmentSelectFilter,
      form.screens.environmentsIds
    )
  )
  const noRowLabelKey =
    totalEnvironments === 0 ? "campaign.environment.not.available" : "campaign.environment.filter.empty"
  const isCreateForm = form.context.isCreation
  const columns: Array<GridColDef<IFormEnvironmentsRow>> = [
    {
      field: "targeted-field-doesnt-exist",
      flex: 1,
      sortable: false,
      minWidth: 70,
      maxWidth: 70,
      cellClassName: "targeted-check",
      renderHeader: () => <ColumnHeader primaryText={translate("label.targeted")} />,
      renderCell: params => {
        const isTargeted = getEnvironmentTreeInfo([], form.screens.environmentsIds, params.row.id).isTargeted
        return (
          <FontAwesomeIcon
            id={
              isTargeted ? `environment-list-${params.row.id}-targeted` : `environment-list-${params.row.id}-untargeted`
            }
            icon={isTargeted ? faCheck : faXmark}
            className={`icon ${isTargeted ? "" : "non-targeted"}`}
          />
        )
      }
    },
    {
      field: "name",
      flex: 4,
      renderHeader: () => <ColumnHeader primaryText={translate("label.name")} />,
      renderCell: params => (
        <Tooltip title={params.row.name}>
          <Typography
            id={`environment-list-${params.row.id}-name`}
            color="textPrimary"
            fontSize=".8rem"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap">
            {params.row.name}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: "impressionRatio",
      flex: 3,
      sortable: false,
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impression.ratio.primary")}
          secondaryText={translate("label.impression.ratio.secondary")}
        />
      ),
      renderCell: params => (
        <ColorizedText
          id={`environment-list-${params.row.id}-impression-ratio`}
          text={params.row.impressionRatio}
          difference={params.row.impressionDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={params.row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "impressionPercent",
      flex: 2,
      renderHeader: () => <ColumnHeader primaryText={translate("label.impression.percent")} />,
      renderCell: params => (
        <ColorizedText
          id={`environment-list-${params.row.id}-impression-percent`}
          text={params.row.impressionPercent === -1 ? "-" : `${params.row.impressionPercent}%`}
          difference={params.row.impressionDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={params.row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "screensRatio",
      flex: 3,
      sortable: false,
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.screen.ratio.primary")}
          secondaryText={translate("label.screen.ratio.secondary")}
        />
      ),
      renderCell: params => (
        <ColorizedText
          id={`environment-list-${params.row.id}-screen-ratio`}
          text={params.row.screensRatio}
          difference={params.row.screensDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={params.row.screens === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "screensPercent",
      flex: 2,
      renderHeader: () => <ColumnHeader primaryText={translate("label.screen.percent")} />,
      renderCell: params => (
        <ColorizedText
          id={`environment-list-${params.row.id}-screen-percent`}
          text={params.row.screensPercent === -1 ? "-" : `${params.row.screensPercent}%`}
          difference={params.row.screensDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={params.row.screens === 0}
          isCreationForm={isCreateForm}
        />
      )
    }
  ]
  return (
    <Box
      sx={{
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-row": {
          cursor: "default"
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "transparent"
        },
        "& .targeted-check": {
          "color": theme => theme.palette.text.primary2,
          "& svg": {
            ml: "1rem"
          }
        },
        "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "transparent !important"
        },
        "& .icon": {
          color: theme => theme.palette.text.primary2
        },
        "& .icon.non-targeted": {
          color: theme => theme.palette.text.secondary
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <DataGrid
        rows={filteredEnvironments}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        className="screens-environments-list"
        rowHeight={30}
        autoPageSize
        localeText={getDataGridTexts(noRowLabelKey)}
        sx={{ mt: "1rem", height: "22rem" }}
      />
    </Box>
  )
})
